import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { GridExternalMessageResponse } from "@/Common/Api/ExternalCommunications/Dto/Responses/GridExternalMessageResponse";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { HyperLinkRedirectControl } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { SpanControl } from "@/Common/Components/SpanControl";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { MyleTableHelper } from "@/Common/Helpers/MyleTableHelper";
import { ExternalMessageOutbox } from "@/Common/Models/ExternalCommunications/GridExternalMessage";
import { ExternalCommunicationsSortOrderType, RedirectTo, SortDirection } from "@/Common/Models/UI/Enums";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ExternalMessageOutboxTableItem } from "@/Communications/Components/ExternalCommunications/ExternalMessageOutboxTableItem";
import { CommunicationsTab_OptimizedV2 } from "../CommunicationsTab_OptimizedV2";
import { ExternalMessageInboxProps } from "./ExternalMessageInboxComponent";
import { ExternalMessageOutboxSettingsContainer, ExternalMessageOutboxSettingsContainerState } from "./ExternalMessageOutboxSettingsContainer";

interface ExternalMessageOutboxProps {
    onMessageRowClick: (message: ExternalMessageOutbox) => void;
    gridSessionId?: string;
}

class ExternalMessageOutboxComponentState {
    public messages: ExternalMessageOutboxTableItem[];

    public searchText: string;
    public currentPageNumber: number;
    public totalQueryPages: number;
    public totalQueryRows: number;
    public selectedPageNumber: number;

    public isSettingsContainerDisplayed: boolean;
    public settings: ExternalMessageOutboxSettingsContainerState;

    public sortingColumn: ExternalCommunicationsSortOrderType;
    public sortingDirection: SortDirection;

    public static getDefault(): ExternalMessageOutboxComponentState {
        const state = new ExternalMessageOutboxComponentState();
        state.messages = [];
        state.searchText = "";
        state.currentPageNumber = 1;
        state.selectedPageNumber = 1;
        state.totalQueryPages = 0;
        state.totalQueryRows = 0;
        state.isSettingsContainerDisplayed = false;
        state.settings = ExternalMessageOutboxSettingsContainerState.createDefault();

        return state;
    }
}

class ExternalMessageOutboxComponent extends BaseReactComponent<ExternalMessageOutboxProps, ExternalMessageOutboxComponentState> {
    constructor(props: ExternalMessageOutboxProps) {
        super(props);

        this.state = ExternalMessageOutboxComponentState.getDefault();
    }

    public componentDidMount(): void {
        this.loadMessages();
        $(window).trigger("resize");
    }

    public componentDidUpdate(prevProps: ExternalMessageInboxProps): void {
        if (this.props.gridSessionId && this.props.gridSessionId !== prevProps.gridSessionId) {
            this.loadMessages();
        }
    }

    public render(): JSX.Element {
        const saveOptionKey = "ExternalMessageOutboxsGridSettings";
        const columnsOptions = cacheStore.Helper.loadTableColumnOptionsFromSettings(saveOptionKey, this.getMyleTableColumnsDefaultOptions());
        const tableColumns = this.getTableColumns(columnsOptions);
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);

        return (
            <div className="workspace">
                <CommonTableHeaderComponent
                    title={`${cacheStore.GetString("ExternalCommunicationsLabel")} : ${cacheStore.GetString("SentFLabel")}`}
                    searchText={this.state.searchText}
                    searchPlaceholder={cacheStore.GetString("SearchInMessagesLabel")}
                    onSearch={text => this.mutateState(s => s.searchText = text, () => this.loadMessages())}
                    onRefreshClick={() => this.loadMessages()}
                    onSettingsClick={() => this.mutateState(s => s.isSettingsContainerDisplayed = !this.state.isSettingsContainerDisplayed)}
                />

                <div id="posref" className="tb ws-scrollable">
                    {this.state.isSettingsContainerDisplayed && (
                        <ExternalMessageOutboxSettingsContainer
                            onCancelClick={() => this.mutateState(s => s.isSettingsContainerDisplayed = false)}
                            onSave={(settings: ExternalMessageOutboxSettingsContainerState) => this.handleSettingsSave(settings)}
                            saveState={this.state.settings}
                        />
                    )}
                    <div className="myle-doc-tb main-content myle-form">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="communications_excomm_outbox_table"
                            Columns={tableColumns}
                            DataSource={this.state.messages.toImmutable()}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            SaveOptionskeyTarget={saveOptionKey}
                            ColumnReorderTarget={null}
                            ColumnSortTarget={null}
                            RowClickFunction={messageItem => { this.props.onMessageRowClick && this.props.onMessageRowClick(messageItem.Model); }}
                        />
                    </div>
                </div>
                <div className={CommunicationsTab_OptimizedV2.footerCSS}>
                    <div className="col-lg-6 col-md-6 col-sm-6">
                        <SimplePagerControl
                            PageSize={this.state.settings.selectedPageSizeOption}
                            TotalRows={this.state.totalQueryRows}
                            CurrentPage={this.state.currentPageNumber}
                            changePageFunction={pageNumber => {
                                this.mutateState(s => s.selectedPageNumber = pageNumber, () => this.loadMessages());
                            }}
                        />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-6 text-right" />
                </div>
            </div>
        );
    }

    private getMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        return [
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("SubjectLabel"),
                FieldName: "Subject",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: ExternalCommunicationsSortOrderType.Subject,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 15,
                sortingMethod: () => this.sortArrayBy(ExternalCommunicationsSortOrderType.Subject)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PatientLabel"),
                FieldName: "PatientName",
                IsSortable: true,
                RowClickEnabled: true,
                SortColumn: ExternalCommunicationsSortOrderType.Patient,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 15,
                sortingMethod: () => this.sortArrayBy(ExternalCommunicationsSortOrderType.Patient)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("FromLabel"),
                FieldName: "From",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: ExternalCommunicationsSortOrderType.From,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 15,
                sortingMethod: () => this.sortArrayBy(ExternalCommunicationsSortOrderType.From)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ToLabel"),
                FieldName: "To",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: ExternalCommunicationsSortOrderType.To,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 15,
                sortingMethod: () => this.sortArrayBy(ExternalCommunicationsSortOrderType.To)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("SentDateLabel"),
                FieldName: "SendDate",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: ExternalCommunicationsSortOrderType.SendDate,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 15,
                sortingMethod: () => this.sortArrayBy(ExternalCommunicationsSortOrderType.SendDate)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ReceivedDateLabel"),
                FieldName: "ReceivedDate",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: ExternalCommunicationsSortOrderType.ReceivedDate,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 15,
                sortingMethod: () => this.sortArrayBy(ExternalCommunicationsSortOrderType.ReceivedDate)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("StatusLabel"),
                FieldName: "SendStatus",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: ExternalCommunicationsSortOrderType.SendStatus,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10,
                sortingMethod: () => this.sortArrayBy(ExternalCommunicationsSortOrderType.SendStatus)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ActionLabel"),
                FieldName: "Action",
                RowClickEnabled: false,
                IsSortable: false,
                IsFixedInPlace: true,
                columnClass: "align-center",
                WidthPixels: 80
            })
        ];
    }

    private buildRow(field: string, isRead: boolean, isFail: boolean): JSX.Element {
        return (
            <div style={this.buildStyle(isRead, isFail)}> <SpanControl title={field} text={field} /> </div>
        );
    }

    private buildStyle(isRead: boolean, isFail: boolean): React.CSSProperties {
        let style: React.CSSProperties = {};
        if (!isRead)
            style.fontWeight = "bold";

        if (isFail)
            style.color = "red";

        return style;
    }

    private getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<ExternalMessageOutboxTableItem>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case "Subject":
                        return {
                            renderCell: item => (
                                this.buildRow(item.Model.subject, item.Model.isRead, item.Model.isFailedSend)
                            )
                        };

                    case "PatientName":
                        return {
                            renderCell: item => {
                                if (item.Model.internalPatientId)
                                    return (
                                        <div>
                                            <HyperLinkRedirectControl
                                                target={RedirectTo.Patient}
                                                redirectValue={item.Model.internalPatientId}
                                                text={item.Model.patientDisplayName}
                                            />
                                        </div>
                                    );

                                return (<div style={this.buildStyle(item.Model.isRead, item.Model.isFailedSend)}>{item.Model.patientDisplayName}</div>);
                            }
                        };

                    case "From":
                        return {
                            renderCell: item => this.buildRow(item.Model.senderDisplayName, item.Model.isRead, item.Model.isFailedSend)
                        };

                    case "To":
                        return {
                            renderCell: item => this.buildRow(item.Model.receiverDisplayName, item.Model.isRead, item.Model.isFailedSend)
                        };

                    case "SendDate":
                        return {
                            renderCell: item => {
                                if (!item.Model.sendDate)
                                    return <div />;

                                const date = moment.utc(item.Model.sendDate).local().format("YYYY/MM/DD");
                                const time = moment.utc(item.Model.sendDate).local().format("HH:mm:ss");
                                const sendDate = `${date} ${cacheStore.GetString("AtLabel")} ${time}`;

                                return this.buildRow(sendDate, item.Model.isRead, item.Model.isFailedSend);
                            }
                        };

                    case "ReceivedDate":
                        return {
                            renderCell: item => {
                                if (!item.Model.receivedDate)
                                    return <div />;

                                const date = moment.utc(item.Model.receivedDate).local().format("YYYY/MM/DD");
                                const time = moment.utc(item.Model.receivedDate).local().format("HH:mm:ss");
                                const receivedDate = item.Model.receivedDate ? `${date} ${cacheStore.GetString("AtLabel")} ${time}` : "";

                                return this.buildRow(receivedDate, item.Model.isRead, item.Model.isFailedSend);
                            }
                        };

                    case "SendStatus":
                        return {
                            renderCell: item => {
                                let sendStatusValue = this.getSendStatusLookupValue(item.Model.sendStatusLookupId);
                                return this.buildRow(sendStatusValue, item.Model.isRead, item.Model.isFailedSend);
                            }
                        };

                    case "Action":
                        return {
                            renderCell: item => {
                                if (!item.Model.isFailedSend)
                                    return <div />;

                                return <a onClick={() => this.handleResend(item.Model.messageId)}>{cacheStore.GetString("ResendLabel")}</a>;
                            }
                        };
                }
            },
            false
        );
    }

    private sortArrayBy(column: ExternalCommunicationsSortOrderType): void {
        this.mutateState(s => {
            s.sortingColumn = column;
            s.sortingDirection = MyleTableHelper.getNewSortDir(column, s.sortingColumn, s.sortingDirection);
            s.messages = s.messages.sortByProperty(
                communicationItem => {
                    let message = communicationItem.Model;

                    switch (column) {
                        case ExternalCommunicationsSortOrderType.Subject:
                            return (message.subject || "").toUpperCase();

                        case ExternalCommunicationsSortOrderType.From:
                            return cacheStore.GetCliniciansPersonsByContactFacetId(message.senderInternalEmployeeId).LastName;

                        case ExternalCommunicationsSortOrderType.To:
                            return message.receiverDisplayName;

                        case ExternalCommunicationsSortOrderType.ReceivedDate:
                            return message.receivedDate;

                        case ExternalCommunicationsSortOrderType.Patient:
                            return message.patientDisplayName;

                        case ExternalCommunicationsSortOrderType.SendDate:
                            return message.sendDate;

                        case ExternalCommunicationsSortOrderType.SendStatus:
                            return this.getSendStatusLookupValue(message.sendStatusLookupId);

                        default:
                            return message.sendDate;
                    }
                },
                this.state.sortingDirection === SortDirection.DESC
            );
        });
    }

    private loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        const defaultTablePluginOptions = {
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: false,
            Autoresize: true,
            OnColumnWidthChange: null
        };

        return Immutable.fromJS(defaultTablePluginOptions);
    }

    private loadMessages(): void {
        let requestData = {
            PageSize: this.state.settings.selectedPageSizeOption,
            PageNumber: this.state.selectedPageNumber,
            ReceivedDateFrom: this.state.settings.receivedDateFrom,
            ReceivedDateTo: this.state.settings.receivedDateTo,
            SendDateFrom: this.state.settings.sendDateFrom,
            SendDateTo: this.state.settings.sendDateTo,
            SearchText: this.state.searchText
        };

        Client.Postv2(
            ServerOperations.Server.Myle_ExternalCommunication_LoadOutboxMessages,
            requestData,
            response => {
                let externalMessageResponse: GridExternalMessageResponse[] = response.Messages;

                this.mutateState(s => {
                    s.currentPageNumber = response.CurrentPageNumber;
                    s.totalQueryPages = response.TotalQueryPages;
                    s.totalQueryRows = response.TotalQueryRows;
                    s.messages = externalMessageResponse.map(x => new ExternalMessageOutboxTableItem(new ExternalMessageOutbox(x)));
                });
            },
            errorMsgs => this.handleError(errorMsgs)
        );
    }

    private handleResend(messageId: string): void {
        let requestData = {
            ExternalMessageId: messageId
        };

        Client.Postv2(
            ServerOperations.Server.Myle_ExternalCommunication_ResendMessage,
            requestData,
            () => this.loadMessages(),
            errorMsgs => this.handleError(errorMsgs)
        );
    }

    private handleError(errorMsgs: ErrorMessage[]): void {
        if (isArrayNullOrUndefinedOrEmpty(errorMsgs))
            return;

        sessionStore.showPopup(errorMsgs);
    }

    private handleSettingsSave(settings: ExternalMessageOutboxSettingsContainerState): void {
        this.mutateState(
            s => {
                if (this.shouldResetToFirstPage(settings))
                    s.selectedPageNumber = 1;

                s.isSettingsContainerDisplayed = false;
                s.settings = settings;
            },
            () => this.loadMessages()
        );
    }

    private shouldResetToFirstPage(settings: ExternalMessageOutboxSettingsContainerState): boolean {
        return this.state.settings.selectedPageSizeOption !== settings.selectedPageSizeOption;
    }

    private getSendStatusLookupValue(sendStatusLookupId: string): string {
        return cacheStore.GetLookupById(sendStatusLookupId)?.GetValue() || "";
    }
}

export { ExternalMessageOutboxComponent };
